import {
  ClientMessage,
  encode,
  type BetCancelRequest,
  type BetCashOutRequest,
  type BetPlaceRequest,
  type Envelope,
  type Slot,
} from './protocol';

/**
 * The WebSocket client.
 *
 * Two things it deliberately does not do:
 *
 *  - It does not decide anything. Every message is handed to a listener as-is;
 *    the multiplier, the payout and the crash point are all the server's.
 *  - It does not reuse a ticket. Tickets are single-use, so a reconnect has to
 *    go back through the handshake for a fresh one. `onNeedsTicket` is how the
 *    socket asks for that.
 */

export type MessageListener = (type: string, data: unknown) => void;

export interface SocketOptions {
  /** Called when a reconnect needs a new single-use ticket. */
  onNeedsTicket: () => Promise<{ url: string; ticket: string }>;
  onMessage: MessageListener;
  onStatus: (status: SocketStatus) => void;
}

export type SocketStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;
const PING_INTERVAL_MS = 20_000;

export class GameSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private closedByUs = false;
  private pingTimer: number | undefined;
  private reconnectTimer: number | undefined;

  constructor(private readonly opts: SocketOptions) {}

  async connect(): Promise<void> {
    this.closedByUs = false;
    this.opts.onStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    let target: { url: string; ticket: string };
    try {
      target = await this.opts.onNeedsTicket();
    } catch {
      this.scheduleReconnect();
      return;
    }

    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${scheme}//${location.host}${target.url}?ticket=${encodeURIComponent(target.ticket)}`;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.opts.onStatus('open');
      this.startPing();
    };

    ws.onmessage = (event) => {
      let envelope: Envelope;
      try {
        envelope = JSON.parse(event.data as string) as Envelope;
      } catch {
        return;
      }
      this.opts.onMessage(envelope.type, envelope.data);
    };

    ws.onclose = () => {
      this.stopPing();
      this.ws = null;
      if (this.closedByUs) {
        this.opts.onStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose always follows, and that is where the reconnect lives.
    };
  }

  close(): void {
    this.closedByUs = true;
    this.stopPing();
    if (this.reconnectTimer !== undefined) window.clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  private send(type: string, data?: unknown): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encode(type, data));
    return true;
  }

  /**
   * Asks for a stake. `nextRound` puts it on the round after the one on screen;
   * which round that is, and what it is worth, stay the server's to decide.
   */
  placeBet(slot: Slot, amount: string, autoCashoutX100?: number, nextRound = false): boolean {
    const request: BetPlaceRequest = {
      slot,
      amount,
      ...(autoCashoutX100 ? { autoCashoutX100 } : {}),
      ...(nextRound ? { nextRound: true } : {}),
    };
    return this.send(ClientMessage.BetPlace, request);
  }

  cancelBet(slot: Slot, nextRound = false): boolean {
    const request: BetCancelRequest = { slot, ...(nextRound ? { nextRound: true } : {}) };
    return this.send(ClientMessage.BetCancel, request);
  }

  /**
   * Cashes out one slot. The slot is the only thing in this message: the bet id,
   * the round, the multiplier and the payout all come from server state, so
   * there is still nothing here worth forging -- the worst a tampered slot can
   * do is settle the sender's own other bet.
   */
  cashOut(slot: Slot): boolean {
    const request: BetCashOutRequest = { slot };
    return this.send(ClientMessage.BetCashOut, request);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => this.send(ClientMessage.Ping), PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== undefined) window.clearInterval(this.pingTimer);
    this.pingTimer = undefined;
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    this.opts.onStatus('reconnecting');

    // Exponential backoff with jitter, so a server restart does not bring every
    // client back in the same millisecond.
    const backoff = Math.min(RECONNECT_BASE_MS * 2 ** (this.attempt - 1), RECONNECT_MAX_MS);
    const jitter = Math.random() * backoff * 0.3;

    this.reconnectTimer = window.setTimeout(() => void this.connect(), backoff + jitter);
  }
}
